import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import { Form, Formik, FormikHelpers } from "formik";
import { object, string } from "yup";
import { LanguageFormValues } from "src/models/Language.model";
import LanguageForm from "../Layout/LanguageForm";
import { showToast } from "src/utils/showToaster";
import {
  useGetLanguagesByIdQuery,
  useEditLanguageMutation,
} from "src/services/LanguageServices";
import CircularProgress from "@mui/material/CircularProgress";

type Props = {
  languageId: string;
  onClose: () => void;
};

const EditLanguageFormWrapper = ({ languageId, onClose }: Props) => {
  const [languageData, setLanguageData] = useState<LanguageFormValues>();

  const { data, isLoading, isFetching } = useGetLanguagesByIdQuery(languageId, {
    skip: !languageId,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      setLanguageData(data?.data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const [editLanguage] = useEditLanguageMutation();
  // Form Initial Values
  const initialValues: LanguageFormValues = {
    languageName: languageData?.languageName || "",
  };

  // Validation Schema
  const validationSchema = object().shape({
    languageName: string().required("Please enter language"),
  });

  // Handle Submit
  const handleSubmit = (
    values: LanguageFormValues,
    { setSubmitting, resetForm }: FormikHelpers<LanguageFormValues>
  ) => {
    setTimeout(() => {
      editLanguage({ id: languageId, body: values }).then((res: any) => {
        if (res?.error) {
          showToast("error", res?.error?.data?.message);
        } else {
          if (res?.data?.status) {
            showToast("success", res?.data?.message);
            resetForm();
            onClose();
          } else {
            showToast("error", res?.data?.message);
          }
        }
        setSubmitting(false);
      });
    }, 1000);
  };

  return (
    <Dialog open maxWidth="xs" fullWidth>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {(formikProps) => (
          <Form>
            {(isLoading || isFetching) && (
              <div className="absolute w-[100%] h-[100%] flex justify-center items-center z-10 bg-slate-100 opacity-50">
                <CircularProgress />
              </div>
            )}
            <LanguageForm
              formType="EDIT"
              formikProps={formikProps}
              onClose={onClose}
            />
          </Form>
        )}
      </Formik>
    </Dialog>
  );
};

export default EditLanguageFormWrapper;
